import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import ResponsiveContainer from '@/components/ResponsiveContainer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '../utils/toast';
import { Save, ArrowLeft, FileText } from 'lucide-react';
import axios from 'axios';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreAssessmentNotesEditorPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const quillRef = useRef(null); // Ref for Quill editor to avoid findDOMNode issue

  // Quill editor modules configuration (toolbar options)
  const modules = useMemo(() => ({
    toolbar: [
      ['bold', 'italic', 'underline'],        // Bold, Italic, Underline
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],  // Numbered and bullet lists
      [{ 'align': [] }],                      // Text alignment
      [{ 'size': ['small', false, 'large', 'huge'] }],  // Font sizes
      [{ 'color': [] }],                      // Text color
      ['link'],                               // Hyperlinks
      ['clean']                               // Remove formatting
    ]
  }), []);

  // Quill editor formats allowed
  const formats = [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'align',
    'size',
    'color',
    'link'
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/signin');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);

    // Check if user has permission
    if (!['Super Admin', 'Exlva Admin'].includes(parsedUser.role)) {
      toast.error('Only Super Admin and Exlva Admin can edit pre-assessment notes');
      navigate('/assessments');
      return;
    }

    fetchNotes();
  }, [navigate]);

  const fetchNotes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assessments/pre-assessment-notes`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const notesContent = response.data.content || '';
      setContent(notesContent);
      setOriginalContent(notesContent);
    } catch (error) {
      console.error('Error fetching pre-assessment notes:', error);
      toast.error('Failed to load pre-assessment notes');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Strip HTML tags to check if there's actual text content
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    if (!textContent) {
      toast.error('Content cannot be empty');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/assessments/pre-assessment-notes`,
        { content: content },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setOriginalContent(content);
      toast.success('Pre-assessment notes updated successfully');
    } catch (error) {
      console.error('Error saving pre-assessment notes:', error);
      toast.error('Failed to save pre-assessment notes');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    const defaultContent = `<p><strong>Important Instructions:</strong></p>
<ul>
<li>Complete the assessment in a single attempt.</li>
<li>If you exit midway, your progress will not be saved and the assessment will restart from the beginning.</li>
<li>Ensure a stable internet connection to avoid interruptions.</li>
<li>Avoid switching tabs or minimizing the screen during the assessment, as it may reset your attempt.</li>
<li>Read each question carefully before submitting your answers.</li>
</ul>`;
    
    setContent(defaultContent);
  };

  const hasChanges = content !== originalContent;

  if (loading) {
    return (
      <AdminLayout>
        <ResponsiveContainer>
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          </div>
        </ResponsiveContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <ResponsiveContainer>
        {/* Header */}
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/assessments')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#0056B3]" />
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900">
                Pre-Assessment Notes
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              className="hidden sm:flex items-center gap-2"
            >
              Reset to Default
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-[#0056B3] hover:bg-[#0056B3]/90 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              <span>{saving ? 'Saving...' : 'Save'}</span>
            </Button>
          </div>
        </div>

        {/* Editor Card */}
        <Card className="p-4 md:p-6">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Edit Pre-Assessment Notes
            </h2>
            <p className="text-sm text-gray-600">
              These notes will be shown to all staff members before they start any assessment.
              Use this to provide important instructions and guidelines.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <div className="bg-white rounded-lg border border-gray-300">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={content}
                  onChange={setContent}
                  placeholder="Enter pre-assessment notes..."
                  modules={modules}
                  formats={formats}
                  className="h-96"
                />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Rich text editor with formatting support
              </p>
            </div>

            {hasChanges && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  ⚠️ You have unsaved changes. Click "Save" to apply them.
                </p>
              </div>
            )}
          </div>

          {/* Mobile Reset Button */}
          <div className="mt-4 sm:hidden">
            <Button
              variant="outline"
              onClick={handleReset}
              className="w-full"
            >
              Reset to Default
            </Button>
          </div>
        </Card>

        {/* Preview Card */}
        <Card className="mt-6 p-4 md:p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
          <div className="bg-gray-50 rounded-lg p-4 md:p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Pre-Assessment Notes</h3>
            <div className="prose prose-sm max-w-none">
              {content.split('\n').map((line, index) => (
                <p key={index} className="mb-2 text-gray-700">
                  {line || '\u00A0'}
                </p>
              ))}
            </div>
          </div>
        </Card>
      </ResponsiveContainer>
    </AdminLayout>
  );
};

export default PreAssessmentNotesEditorPage;
